/**
 * connectorMeta
 * =============
 *
 * Single source of truth for everything "data source" related across the UI:
 * logos, friendly labels, the connector picker, connection-string templates,
 * sync-frequency presets, and the column-type vocabulary.
 *
 * Every data-source page imports from here so logos, labels, and options stay
 * identical whether you're looking at the list, the connect flow, the detail
 * page, or an edit screen.
 */

import React from 'react';
import { Box, useTheme, alpha } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import ApiIcon from '@mui/icons-material/Api';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BoltIcon from '@mui/icons-material/Bolt';
import CloudIcon from '@mui/icons-material/Cloud';
import WebhookIcon from '@mui/icons-material/Webhook';

import bigqueryLogo from '../../images/datasources/bigquery.svg';
import kafkaLogo from '../../images/datasources/kafka.svg';
import mysqlLogo from '../../images/datasources/mysql.svg';
import postgreLogo from '../../images/datasources/postgre.svg';
import s3Logo from '../../images/datasources/s3.svg';
import mssqlLogo from '../../images/datasources/mssql.svg';
import webhookLogo from '../../images/datasources/webhook.svg';
import stripeLogo from '../../images/datasources/stripe.svg';
import shopifyLogo from '../../images/datasources/shopify.svg';
import googleSheetsLogo from '../../images/datasources/googlesheets.svg';
import googleAnalyticsLogo from '../../images/datasources/googleanalytics.svg';
import hubspotLogo from '../../images/datasources/hubspot.svg';
import squareLogo from '../../images/datasources/square.svg';
import quickbooksLogo from '../../images/datasources/quickbooks.svg';
import salesforceLogo from '../../images/datasources/salesforce.svg';
import coinbaseLogo from '../../images/datasources/coinbase.svg';

// connector_type -> svg logo (where we have a real brand mark)
export const LOGO_MAP = {
  bigquery: bigqueryLogo,
  kafka: kafkaLogo,
  mysql: mysqlLogo,
  postgres: postgreLogo,
  postgresql: postgreLogo,
  s3: s3Logo,
  mssql: mssqlLogo,
  sqlserver: mssqlLogo,
  webhook: webhookLogo,
  stripe: stripeLogo,
  shopify: shopifyLogo,
  googlesheets: googleSheetsLogo,
  googleanalytics: googleAnalyticsLogo,
  hubspot: hubspotLogo,
  square: squareLogo,
  quickbooks: quickbooksLogo,
  salesforce: salesforceLogo,
  coinbase: coinbaseLogo,
};

// Fallback MUI icon for connectors without a brand svg (keyed by connector_type
// AND by event-tile id so the picker shows the right glyph).
const FALLBACK_ICON = {
  rest: ApiIcon,
  rest_events: ApiIcon,
  graphql: ApiIcon,
  file: InsertDriveFileIcon,
  kafka: BoltIcon,
  webhook: WebhookIcon,
  twitter: BoltIcon,
  s3: CloudIcon,
};

export const TYPE_LABEL = {
  postgres: 'PostgreSQL',
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  sqlserver: 'SQL Server',
  mssql: 'SQL Server',
  bigquery: 'BigQuery',
  s3: 'Amazon S3',
  kafka: 'Kafka',
  rest: 'REST API',
  graphql: 'GraphQL',
  webhook: 'Webhook',
  file: 'File',
  stripe: 'Stripe',
  shopify: 'Shopify',
  googlesheets: 'Google Sheets',
  googleanalytics: 'Google Analytics',
  hubspot: 'HubSpot',
  square: 'Square',
  quickbooks: 'QuickBooks',
  salesforce: 'Salesforce',
  coinbase: 'Coinbase',
};

export const typeLabel = (t) => TYPE_LABEL[t] || (t ? t.toUpperCase() : 'Source');

/**
 * ConnectorLogo - renders the brand svg in a soft rounded tile, falling back
 * to a tinted MUI icon. Used everywhere a source needs an avatar.
 */
export const ConnectorLogo = ({ type, size = 32, tile = true }) => {
  const theme = useTheme();
  const logo = LOGO_MAP[type];
  const inner = Math.round(size * 0.62);

  const content = logo ? (
    <img src={logo} alt={type} style={{ height: inner, width: 'auto', objectFit: 'contain' }} />
  ) : (
    (() => {
      const Icon = FALLBACK_ICON[type] || StorageIcon;
      return <Icon sx={{ fontSize: inner, color: theme.palette.text.secondary }} />;
    })()
  );

  if (!tile) return content;
  return (
    <Box sx={{
      width: size, height: size, borderRadius: 1.5, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.03),
      border: `1px solid ${theme.palette.divider}`,
    }}>
      {content}
    </Box>
  );
};

// One-click app connectors ("Connect with ..."). `live` apps start the OAuth
// flow; `coming_soon` apps render as disabled catalog tiles. The id doubles as
// the backend connector_type / OAuth provider id.
export const APP_CONNECTORS = [
  { id: 'stripe',          label: 'Stripe',           tagline: 'Revenue, churn & cash flow',   status: 'live' },
  { id: 'shopify',         label: 'Shopify',          tagline: 'Orders, products & demand',    status: 'coming_soon' },
  { id: 'googlesheets',    label: 'Google Sheets',    tagline: 'Any spreadsheet, kept live',   status: 'coming_soon' },
  { id: 'googleanalytics', label: 'Google Analytics', tagline: 'Traffic & conversions',        status: 'coming_soon' },
  { id: 'hubspot',         label: 'HubSpot',          tagline: 'Pipeline & deal forecasts',    status: 'coming_soon' },
  { id: 'square',          label: 'Square',           tagline: 'In-store sales & POS',         status: 'coming_soon' },
  { id: 'quickbooks',      label: 'QuickBooks',       tagline: 'Cash flow & expenses',         status: 'coming_soon' },
  { id: 'salesforce',      label: 'Salesforce',       tagline: 'CRM & sales forecasts',        status: 'coming_soon' },
  { id: 'coinbase',        label: 'Coinbase',         tagline: 'Crypto portfolio',             status: 'coming_soon' },
];

// The connector picker (what the "new source" flow shows). `string` connectors
// accept a single pasted connection string; `webhook` is push (no string).
// Events are NOT a separate source type - any source's field mapper has an
// "event" checkbox to mark which fields are events.
export const CONNECTOR_TYPES = [
  { id: 'postgres',  label: 'PostgreSQL', category: 'Database',  string: true,  placeholder: 'postgresql://user:password@host:5432/database' },
  { id: 'mysql',     label: 'MySQL',      category: 'Database',  string: true,  placeholder: 'mysql://user:password@host:3306/database' },
  { id: 'sqlserver', label: 'SQL Server', category: 'Database',  string: true,  placeholder: 'sqlserver://user:password@host:1433/database' },
  { id: 'bigquery',  label: 'BigQuery',   category: 'Warehouse', string: true,  placeholder: 'bigquery://project-id/dataset' },
  { id: 's3',        label: 'Amazon S3',  category: 'Storage',   string: true,  placeholder: 's3://bucket/optional/prefix' },
  { id: 'rest',      label: 'REST API',   category: 'API',       string: true,  placeholder: 'https://api.example.com/v1/endpoint' },
  { id: 'kafka',     label: 'Kafka',      category: 'Streaming', string: true,  placeholder: 'kafka://broker:9092/topic' },
  { id: 'webhook',   label: 'Webhook',    category: 'Streaming', string: false, placeholder: '' },
];

export const tileMeta = (tileId) => CONNECTOR_TYPES.find((x) => x.id === tileId) || null;

// Friendly sync cadences shown as a segmented control.
export const FREQUENCIES = [
  { id: 'realtime', label: 'Every minute', hint: 'polls once a minute' },
  { id: '15m',      label: '15 min',       hint: 'every 15 minutes' },
  { id: 'hourly',   label: 'Hourly',       hint: 'once an hour' },
  { id: 'daily',    label: 'Daily',        hint: 'once a day' },
  { id: 'manual',   label: 'Manual',       hint: 'only when you trigger it' },
];

export const frequencyLabel = (id) => (FREQUENCIES.find((f) => f.id === id) || {}).label || id;

// Schedule shape <-> friendly id (mirrors the backend _frequency_to_schedule).
export const scheduleToFrequency = (schedule = {}) => {
  if (!schedule || schedule.enabled === false) return 'manual';
  if (schedule.type === 'interval') {
    const s = Number(schedule.interval_seconds) || 0;
    if (s <= 60) return 'realtime';
    if (s <= 900) return '15m';
    if (s <= 3600) return 'hourly';
    return 'daily';
  }
  if (schedule.type === 'cron') {
    const c = schedule.cron_expression || '';
    if (c === '0 * * * *') return 'hourly';
    if (c.startsWith('0 2')) return 'daily';
    return 'hourly';
  }
  return 'manual';
};

// The column-type vocabulary the user can pick from (compact, human labels
// over the platform's universal type taxonomy).
export const TYPE_OPTIONS = [
  { value: 'continuous_numerical', label: 'Number' },
  { value: 'discrete_numerical',   label: 'Integer' },
  { value: 'binary_boolean',       label: 'Boolean' },
  { value: 'datetime_timestamp',   label: 'Date/Time' },
  { value: 'nominal_categorical',  label: 'Category' },
  { value: 'categorical_text',     label: 'Text' },
];

// --- plan-budget estimation (warn before a backfill exceeds the plan) ------
export const tableSignalCols = (t) =>
  (t.columns || []).filter((c) => c.included && !c.is_timestamp && !c.is_entity).length;

export const tableEstSignals = (t) =>
  (t.row_estimate == null ? null : t.row_estimate * Math.max(1, tableSignalCols(t)));

export const totalEstSignals = (tables, selected) =>
  tables.filter((t) => selected[t.table_name])
    .reduce((acc, t) => acc + (tableEstSignals(t) || 0), 0);

// Distribute the remaining signal budget across the selected tables and return
// a per-table row cap (the MOST RECENT N rows we'll backfill). Proportional to
// each table's size so big tables don't starve small ones.
export const computeBackfillLimits = (tables, selected, remaining) => {
  const sel = tables.filter((t) => selected[t.table_name] && tableEstSignals(t) != null);
  const totalSignals = sel.reduce((a, t) => a + tableEstSignals(t), 0);
  const out = {};
  if (!sel.length || !remaining || totalSignals <= 0) return out;
  sel.forEach((t) => {
    const share = tableEstSignals(t) / totalSignals;       // 0..1
    const signalBudget = Math.max(1, Math.floor(remaining * share));
    const cols = Math.max(1, tableSignalCols(t));
    out[t.table_name] = Math.max(1, Math.floor(signalBudget / cols));
  });
  return out;
};

// Translate a per-table ImportScope into the backend backfill payload.
// `fitLimit` is the plan-fit row cap to use when the scope is left on "auto".
export const scopeToBackfill = (scope, fitLimit) => {
  const m = scope?.mode || 'auto';
  if (m === 'recent' && scope?.limit) return { backfill_limit: scope.limit };
  if (m === 'range' && (scope?.from || scope?.to)) {
    return { backfill_range: { from: scope.from || null, to: scope.to || null } };
  }
  return fitLimit ? { backfill_limit: fitLimit } : {};
};

export const compactNum = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n}`;
};

// Map an auto-pause kind to a short, human reason.
export const PAUSE_REASON_LABEL = (kind) => {
  switch (kind) {
    case 'signal_types':
    case 'signals':
    case 'plan':   return 'Plan limit reached';
    case 'schema': return 'Schema error';
    case 'config': return 'Config error';
    default:       return 'Auto-paused';
  }
};
